import { defineComponent as _defineComponent } from 'vue'
import VueI18n from "vue-i18n";
import { Dictionary, RawLocation } from "vue-router/types/router";
import { METADATA_TYPE } from "@/views/cms/metadata-item/common/choices";
import { Env, Nullable } from "@/common/types";
import { Permission } from "@/common/permissions";
import { envs } from "@/const/dotenv";
import { Region } from "@/views/cms/content/common/choices";
import { AppVersionRoutes } from "@/views/app-version/common/types";
import i18n from "@/plugins/vue-i18n";
import store from "@/store";
import router from "@/router";
import { useVModel } from "@vueuse/core";
import Vuetify from "@/plugins/vuetify";

interface MenuItem {
  text: VueI18n.TranslateResult;
  icon?: string;
  endIcon?: string;
  path?: string;
  query?: Dictionary<string | Nullable<string>[] | null | undefined>;
  children?: MenuItem[];
  permission?: string;
  availableRegions?: string[];
  availableEnvs?: string[];
}


export default /*#__PURE__*/_defineComponent({
  __name: 'NavigationDrawer',
  props: {
    value: { type: Boolean }
  },
  emits: ["input"],
  setup(__props: any, { emit: emits }: { emit: ({ (e: "input", v: boolean): void }), expose: any, slots: any, attrs: any }) {

const props = __props as { value: boolean }



const value = useVModel(props, "value", emits, { eventName: "input" });
const menuItems: MenuItem[] = [
  { text: i18n.t("menu.home"), icon: "home", path: "/" },
  {
    icon: "collections",
    text: i18n.t("menu.cms"),
    permission: Permission.MENU_CMS,
    children: [
      {
        text: i18n.t("menu.contents"),
        path: "/contents",
        permission: Permission.READ_CONTENT_AUTHOR,
      },
      {
        text: i18n.t("menu.author"),
        path: "/authors",
        permission: Permission.READ_CONTENT_AUTHOR,
      },
      {
        text: i18n.t("menu.submissionEpisodes"),
        path: "/submission-episodes",
        permission: Permission.READ_CONTENT_AUTHOR,
        availableRegions: [Region.KOREA],
      },
      {
        text: i18n.t("menu.metadata"),
        permission: Permission.READ_METADATA_ITEM,
        children: METADATA_TYPE.getChoices().map((metadata) => {
          return {
            text: metadata.text,
            path: "/metadata-items",
            query: {
              metadata: metadata.value,
            },
          };
        }),
      },
    ],
  },
  {
    icon: "mdi-developer-board",
    text: i18n.t("menu.sms"),
    permission: Permission.MENU_SMS,
    children: [
      {
        text: i18n.t("menu.sections"),
        path: "/sections",
      },
      {
        text: i18n.t("menu.eventPopup"),
        path: "/event-popups",
      },
      {
        text: i18n.t("menu.bridgePage"),
        path: "/bridge-pages",
      },
      {
        text: i18n.t("menu.matching"),
        path: "/matching-collections",
      },
    ],
  },
  {
    icon: "mdi-target-account",
    text: i18n.t("menu.reward"),
    permission: Permission.MENU_REWARD,
    children: [
      {
        text: i18n.t("menu.present"),
        path: "/presents",
        permission: Permission.READ_EDIT_PRESENT,
      },
      {
        text: i18n.t("menu.raffle"),
        path: "/raffles",
        permission: Permission.READ_EDIT_RAFFLE,
      },
      {
        text: i18n.t("menu.luckyDraw"),
        path: "/lucky-draws",
        permission: Permission.READ_EDIT_LUCKY_DRAW,
      },
    ],
  },
  {
    icon: "mdi-crosshairs-gps",
    text: i18n.t("menu.crm"),
    permission: Permission.MENU_CRM,
    children: [
      {
        text: i18n.t("menu.messagePromotion"),
        path: "/message-promotions",
        permission: Permission.READ_MSG_PROMO,
      },
      {
        text: i18n.t("menu.segmentTicker"),
        path: "/segment-tickers",
        permission: Permission.READ_SEGMENT_TICKER,
      },
      {
        text: i18n.t("menu.questCampaign"),
        path: "/quest-campaigns",
        permission: Permission.READ_QUEST_CAMPAIGN,
      },
      {
        text: `${i18n.t("menu.redeemCodeCampaign")} v1`,
        path: "/redeem-campaigns",
        permission: Permission.READ_REDEEM_CAMPAIGN,
      },
      {
        text: `${i18n.t("menu.redeemCodeCampaign")} v2`,
        path: "/v2/redeem-campaigns",
        permission: Permission.READ_REDEEM_CAMPAIGN,
        availableRegions: [Region.KOREA],
      },
      {
        text: i18n.t("menu.contentDiscountCampaign"),
        path: "/ticket-discounts",
        permission: Permission.READ_TICKET_DISCOUNT_CAMPAIGN,
      },
      {
        text: i18n.t("menu.ticketGiftbox"),
        path: "/giftbox-tickets",
        permission: Permission.READ_TICKET_GIFT_BOX,
      },
      {
        text: i18n.t("menu.giftPromotion"),
        path: "/gift-promotions?id=1",
        permission: Permission.READ_GIFT_PROMOTION,
        availableRegions: [Region.KOREA],
      },
    ],
  },
  {
    icon: "mdi-alert-octagram-outline",
    text: i18n.t("menu.noticeCs"),
    permission: Permission.MENU_NOTICE_CS,
    children: [
      {
        text: i18n.t("menu.notice"),
        path: "/notices",
        permission: Permission.READ_EDIT_NOTICE,
      },
      {
        text: i18n.t("menu.faq"),
        path: "/faqs",
        permission: Permission.READ_EDIT_FAQ,
      },
      {
        text: i18n.t("menu.noticePopup"),
        path: "/notice-popups",
        permission: Permission.READ_EDIT_POPUP_NOTIFICATION,
      },
    ],
  },
  {
    icon: "mdi-account",
    text: i18n.t("menu.memberManagement"),
    permission: Permission.MENU_MEMBER,
    children: [
      {
        text: i18n.t("menu.memberSearch"),
        path: "/member-search",
        permission: Permission.READ_MEMBER,
      },
      {
        text: i18n.t("menu.serviceBlockUserManagement"),
        path: "/service-block",
        permission: Permission.READ_BLOCKED_MEMBER,
      },
      {
        text: i18n.t("menu.nicknameProhibitionManagement"),
        path: "/nickname-prohibition",
        permission: Permission.READ_EDIT_PROHIBITION_NICKNAME,
      },
    ],
  },
  {
    icon: "mdi-comment-search-outline",
    text: i18n.t("menu.commentSystem"),
    permission: Permission.MENU_COMMENT,
    children: [
      {
        text: i18n.t("menu.commentReport"),
        path: "/reports",
        permission: Permission.READ_EDIT_REPORT_COMMENT,
        availableRegions: [Region.THAILAND, Region.TAIWAN, Region.INDONESIA],
      },
      {
        text: i18n.t("menu.comment"),
        path: "/comments",
        permission: Permission.READ_EDIT_COMMENT,
      },
      {
        text: i18n.t("menu.commentBannedUser"),
        path: "/banned-users",
        permission: Permission.READ_EDIT_COMMENT_BLOCK_USER,
      },
      {
        text: i18n.t("menu.commentBannedWord"),
        path: "/banned-words",
        permission: Permission.READ_EDIT_PROHIBITION_COMMENT,
        availableRegions: [Region.THAILAND, Region.TAIWAN, Region.INDONESIA],
      },
    ],
  },
  {
    icon: "mdi-account-outline",
    text: i18n.t("menu.userManagement"),
    permission: Permission.EDIT_ADMIN,
    children: [
      {
        text: i18n.t("menu.users"),
        path: "/users",
        permission: Permission.REGISTER_ADMIN,
      },
      {
        text: i18n.t("menu.roles"),
        path: "/roles",
        permission: Permission.EDIT_ADMIN,
      },
    ],
  },
  {
    icon: "mdi-apps",
    text: i18n.t("menu.appVersion"),
    permission: Permission.MENU_APP_VERSION,
    children: [
      {
        text: i18n.t("menu.korProdAppVersion"),
        path: `/${"kor-prod-app-version" as AppVersionRoutes}`,
        permission: Permission.READ_EDIT_APP_VERSION,
        availableEnvs: [Env.PROD, Env.LOCAL],
      },
      {
        text: i18n.t("menu.globalProdAppVersion"),
        path: `/${"global-prod-app-version" as AppVersionRoutes}`,
        permission: Permission.READ_EDIT_APP_VERSION,
        availableEnvs: [Env.PROD, Env.LOCAL],
      },
      {
        text: i18n.t("menu.korQAAppVersion"),
        path: `/${"kor-qa-app-version" as AppVersionRoutes}`,
        permission: Permission.READ_EDIT_APP_VERSION,
      },
      {
        text: i18n.t("menu.globalQAAppVersion"),
        path: `/${"global-qa-app-version" as AppVersionRoutes}`,
        permission: Permission.READ_EDIT_APP_VERSION,
      },
      {
        text: i18n.t("menu.korSandboxAppVersion"),
        path: `/${"kor-sandbox-app-version" as AppVersionRoutes}`,
        permission: Permission.READ_EDIT_APP_VERSION,
      },
      {
        text: i18n.t("menu.globalSandboxAppVersion"),
        path: `/${"global-sandbox-app-version" as AppVersionRoutes}`,
        permission: Permission.READ_EDIT_APP_VERSION,
      },
    ],
    availableRegions: [Region.KOREA],
  },
  {
    icon: "mdi-tools",
    text: i18n.t("menu.util"),
    permission: Permission.READ_EDIT_UTILITY,
    children: [
      {
        text: i18n.t("menu.converter"),
        path: "/converter",
      },
    ],
  },
];

function hasPermission(menuItem: MenuItem): boolean {
  if (envs.SKIP_AUTH) {
    return true;
  }

  return (
    !menuItem.permission ||
    (Array.isArray(store.getters.permissions) &&
      store.getters.permissions.includes(menuItem.permission))
  );
}

function isAvailableRegion(menuItem: MenuItem): boolean {
  if (!menuItem.availableRegions) {
    return true;
  }

  return menuItem.availableRegions.includes(envs.SERVICE_REGION_CODE);
}

function isAvailableEnv(menuItem: MenuItem): boolean {
  if (!menuItem.availableEnvs) {
    return true;
  }

  return !!menuItem.availableEnvs.find((env) => envs.ENVIRONMENT.includes(env));
}

function go(menuItem: MenuItem): void {
  const currentRoute = router.currentRoute;
  if (
    menuItem.path &&
    (currentRoute.path !== menuItem.path ||
      (menuItem.query &&
        JSON.stringify(currentRoute.query) !== JSON.stringify(menuItem.query)))
  ) {
    router.push({
      path: menuItem.path,
      query: { ...menuItem.query },
    } as RawLocation);
  }
}

return { __sfc: true,props, emits, value, menuItems, hasPermission, isAvailableRegion, isAvailableEnv, go, Vuetify }
}

})